import React, { FC } from 'react';

import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';
// icons
import ICONS from '../../constants/icons';

// helpers
import { BaseError } from '../../helpers/BaseError';
import confirm from '../../helpers/confirm';

// models
import { FormulaDetailModel, FormulaStatus } from '../../models/formula.model';

// components
import IconButton from '../buttons/iconButton';

interface FormulaActionProps {
    rowData: FormulaDetailModel;
    deleteFormula: UseMutationResult<
        AxiosResponse<FormulaDetailModel>,
        BaseError,
        number
    >;
    archiveFormula: UseMutationResult<
        AxiosResponse<FormulaDetailModel>,
        BaseError,
        number
    >;
    isClonePending: boolean;
    selectFormulaToClone: (value: number) => void;
    hasDeleteAccess: boolean;
}

const FormulaAction: FC<React.PropsWithChildren<FormulaActionProps>> = ({
    rowData,
    deleteFormula,
    archiveFormula,
    selectFormulaToClone,
    isClonePending,
    hasDeleteAccess,
}) => {
    const handleArchive = async (e: any) => {
        e.stopPropagation();
        const confirmed = await confirm(
            'Are you sure you want to archive this message?',
            undefined,
            undefined,
            undefined,
            'Archive',
        );
        if (confirmed) {
            archiveFormula.mutate(rowData.id, {
                onSuccess: () => {
                    setTimeout(archiveFormula.reset, 2000);
                },
            });
        }
    };
    const handleDelete = async (e: any) => {
        e.stopPropagation();
        const confirmed = await confirm(
            'Are you sure you want to delete this message?',
            'warning',
            undefined,
            undefined,
            'Delete message',
        );
        if (confirmed) {
            deleteFormula.mutate(rowData.id, {
                onSuccess: () => {
                    setTimeout(deleteFormula.reset, 2000);
                },
            });
        }
    };

    return (
        <Container>
            <IconButton
                tooltipPlacement="bottom-end"
                Icon={ICONS.copy}
                tooltip={'Clone Message'}
                color={aampeTheme.colors.solidA01}
                action={() => selectFormulaToClone(rowData.id)}
                size={20}
                disabled={isClonePending}
            />
            {rowData?.status !== FormulaStatus.Archived && (
                <>
                    {rowData?.status === FormulaStatus.Approved &&
                    rowData.has_write_access ? (
                        <IconButton
                            size={20}
                            tooltipPlacement="bottom-end"
                            Icon={ICONS.archive}
                            tooltip="Archive Message"
                            color={aampeTheme.colors.solidA01}
                            action={handleArchive}
                            disabled={isClonePending}
                        />
                    ) : (
                        hasDeleteAccess && (
                            <IconButton
                                size={20}
                                tooltipPlacement="bottom-end"
                                Icon={ICONS.trash}
                                tooltip="Delete Message"
                                color={aampeTheme.colors.solidA01}
                                action={handleDelete}
                                disabled={isClonePending}
                            />
                        )
                    )}
                </>
            )}
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: 'row',
    gap: aampeTheme.spacing(2),
    alignItems: 'center',
    marginTop: aampeTheme.spacing(0.5),
});

export default FormulaAction;
